import { useTranslation } from 'react-i18next'
import { LegacyAudioSettingsPopover } from './LegacyAudioSettingsPopover'
import {
  LEGACY_AUDIO_LOOP_OPTIONS,
  LEGACY_AUDIO_SPEED_OPTIONS,
  type LegacyAudioSettingsSection,
} from './useLegacyAudioController'

interface LegacyAudioSettingsProps {
  readonly anchorEl: HTMLButtonElement | null
  readonly id?: string
  readonly loopLevel: number
  readonly onChangeLoop: (loopLevel: number) => void
  readonly onChangeSection: (section: LegacyAudioSettingsSection) => void
  readonly onChangeSpeed: (playbackRate: number) => void
  readonly onClose: () => void
  readonly panelClassName?: string
  readonly paperClassName?: string
  readonly playbackRate: number
  readonly section: LegacyAudioSettingsSection
  readonly transitionDuration?: number
}

function getSpeedLabel(playbackRate: number, normalLabel: string) {
  return playbackRate === 1 ? normalLabel : `${playbackRate}x`
}

export function LegacyAudioSettings({
  anchorEl,
  id,
  loopLevel,
  onChangeLoop,
  onChangeSection,
  onChangeSpeed,
  onClose,
  panelClassName = '',
  paperClassName = '',
  playbackRate,
  section,
  transitionDuration = 0,
}: LegacyAudioSettingsProps) {
  const { t } = useTranslation('book')
  const speedLabel = t('Speed')
  const loopLabel = t('Loop')
  const normalLabel = t('Normal')

  return (
    <LegacyAudioSettingsPopover
      anchorEl={anchorEl}
      onClose={onClose}
      paperClassName={`legacyAudioSettingsPopoverPaper legacyAudioSettingsPopover ${paperClassName}`.trim()}
      transitionDuration={transitionDuration}
    >
      <div
        className={`boxSpeed legacySourceAudioSettingsBoxSpeed ${panelClassName}`.trim()}
        id={id}
      >
        {section === 'speed' ? (
          <div>
            <button
              className="btn-details legacyAudioSettingBack"
              onClick={() => onChangeSection('menu')}
              type="button"
            >
              <span className="btn-content">{speedLabel}</span>
            </button>
            <div className="menu legacyAudioSettingMenu">
              {LEGACY_AUDIO_SPEED_OPTIONS.map((rate) => (
                <button
                  className={`btnMenu legacyAudioSettingOption${playbackRate === rate ? ' current' : ''}`}
                  key={rate}
                  onClick={() => onChangeSpeed(rate)}
                  type="button"
                >
                  <span>{getSpeedLabel(rate, normalLabel)}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {section === 'loop' ? (
          <div>
            <button
              className="btn-details legacyAudioSettingBack"
              onClick={() => onChangeSection('menu')}
              type="button"
            >
              <span className="btn-content">{loopLabel}</span>
            </button>
            <div className="menu legacyAudioSettingMenu">
              {LEGACY_AUDIO_LOOP_OPTIONS.map((level) => (
                <button
                  className={`btnMenu legacyAudioSettingOption${loopLevel === level ? ' current' : ''}`}
                  key={level}
                  onClick={() => onChangeLoop(level)}
                  type="button"
                >
                  <span>{level}x</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div style={{ display: section === 'menu' ? 'block' : 'none' }}>
          <div className="menu legacyAudioSettingMenu">
            <button
              className="btn legacyAudioSettingDrill"
              onClick={() => onChangeSection('speed')}
              type="button"
            >
              <span className="btn-content legacyAudioSettingDrillContent">
                {speedLabel}
                <span className="btn-content-value legacyAudioSettingDrillValue">
                  {getSpeedLabel(playbackRate, normalLabel)}
                </span>
              </span>
            </button>
            <button
              className="btn legacyAudioSettingDrill"
              onClick={() => onChangeSection('loop')}
              type="button"
            >
              <span className="btn-content legacyAudioSettingDrillContent">
                {loopLabel}
                <span className="btn-content-value legacyAudioSettingDrillValue">
                  {loopLevel}x
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </LegacyAudioSettingsPopover>
  )
}
